/**
 * StudentForm.js — Public student self-fill profile form
 * URL: /student-form/{companyId}/{leadId}/{token}
 * No Firebase Auth required — the URL token is the gate.
 */
import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function parseRoute() {
  const parts = window.location.pathname.split('/');
  const companyId = parts[2] || '';
  const segment3  = parts[3] || '';
  const token     = parts[4] || '';
  // /student-form/{companyId}/intake/{token}  → intake mode
  // /student-form/{companyId}/{leadId}/{token} → per-student mode
  const isIntake  = segment3 === 'intake';
  const leadId    = isIntake ? 'intake' : segment3;
  return { companyId, leadId, token, isIntake };
}

const STEPS = [
  { id: 'personal',   label: 'Personal',        icon: '👤' },
  { id: 'parents',    label: 'Parents',          icon: '👨‍👩‍👧' },
  { id: 'academic',   label: 'Academics',        icon: '🏫' },
  { id: 'tests',      label: 'Test Scores',      icon: '📝' },
  { id: 'objective',  label: 'Objective',        icon: '🎯' },
  { id: 'media',      label: 'Photo & Story',    icon: '📸' },
];

const BLANK_EDU_HISTORY = [
  { level: 'Grade 9',           school: '', location: '', fromDate: '', toDate: '', curriculum: '', marks: '' },
  { level: 'Grade 10',          school: '', location: '', fromDate: '', toDate: '', curriculum: '', marks: '' },
  { level: 'Grade 11',          school: '', location: '', fromDate: '', toDate: '', curriculum: '', marks: '' },
  { level: 'Grade 12',          school: '', location: '', fromDate: '', toDate: '', curriculum: '', marks: '' },
  { level: 'College/University',school: '', location: '', fromDate: '', toDate: '', curriculum: '', marks: '' },
  { level: 'Others',            school: '', location: '', fromDate: '', toDate: '', curriculum: '', marks: '' },
];

// ── Shared UI ─────────────────────────────────────────────────────────────────
const inputCss = { width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#fff', color: '#0f172a', boxSizing: 'border-box', fontFamily: 'inherit' };
const focusStyle = e => { e.target.style.borderColor = '#7c3aed'; };
const blurStyle  = e => { e.target.style.borderColor = '#e2e8f0'; };

function F({ label, required, children, half }) {
  return (
    <div style={{ marginBottom: 14, gridColumn: half ? 'span 1' : undefined }}>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Inp({ value, onChange, placeholder, type = 'text' }) {
  return <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputCss} onFocus={focusStyle} onBlur={blurStyle} />;
}

function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputCss, appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2364748b\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 32 }} onFocus={focusStyle} onBlur={blurStyle}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Grid({ children, cols = 2 }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>{children}</div>;
}

function Card({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 28px rgba(0,0,0,0.07)', padding: '24px 24px 20px' }}>
      {title && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>{title}</h2>
          {subtitle && <p style={{ fontSize: 12, color: '#64748b', marginTop: 5, lineHeight: 1.6 }}>{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 14px' }}>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
      {label && <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>{label}</span>}
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

// ── Education History table row ───────────────────────────────────────────────
function EduRow({ row, onChange }) {
  const upd = (k, v) => onChange({ ...row, [k]: v });
  const td = (content, width) => (
    <td style={{ padding: '4px 5px', verticalAlign: 'top', width }}>
      {content}
    </td>
  );
  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      {td(<div style={{ fontSize: 12, fontWeight: 600, color: '#475569', padding: '8px 4px', whiteSpace: 'nowrap' }}>{row.level}</div>, 120)}
      {td(<input value={row.school} onChange={e => upd('school', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="School / Institution Name" onFocus={focusStyle} onBlur={blurStyle} />, undefined)}
      {td(<input value={row.location} onChange={e => upd('location', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="City, State" onFocus={focusStyle} onBlur={blurStyle} />, 120)}
      {td(<input value={row.fromDate} onChange={e => upd('fromDate', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="MM/YYYY" onFocus={focusStyle} onBlur={blurStyle} />, 90)}
      {td(<input value={row.toDate} onChange={e => upd('toDate', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="MM/YYYY" onFocus={focusStyle} onBlur={blurStyle} />, 90)}
      {td(<input value={row.curriculum} onChange={e => upd('curriculum', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="CBSE / IB…" onFocus={focusStyle} onBlur={blurStyle} />, 100)}
      {td(<input value={row.marks} onChange={e => upd('marks', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="e.g. 92%" onFocus={focusStyle} onBlur={blurStyle} />, 80)}
    </tr>
  );
}

// ── AP Test row ───────────────────────────────────────────────────────────────
function APRow({ row, onChange, onRemove, showRemove }) {
  const upd = (k, v) => onChange({ ...row, [k]: v });
  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '4px 5px' }}>
        <input value={row.subject} onChange={e => upd('subject', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="Subject (e.g. Calculus AB)" onFocus={focusStyle} onBlur={blurStyle} />
      </td>
      <td style={{ padding: '4px 5px', width: 120 }}>
        <input value={row.score} onChange={e => upd('score', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="Score (1–5)" onFocus={focusStyle} onBlur={blurStyle} />
      </td>
      <td style={{ padding: '4px 5px', width: 130 }}>
        <input value={row.dateTaken} onChange={e => upd('dateTaken', e.target.value)} style={{ ...inputCss, fontSize: 12, padding: '7px 9px' }} placeholder="MM/YYYY" onFocus={focusStyle} onBlur={blurStyle} />
      </td>
      {showRemove && (
        <td style={{ padding: '4px 5px', width: 36 }}>
          <button onClick={onRemove} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </td>
      )}
    </tr>
  );
}

// ── Checkbox group ────────────────────────────────────────────────────────────
function CheckGroup({ label, options, selected, onChange }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <F label={label}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {options.map(o => (
          <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#334155', userSelect: 'none' }}>
            <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} style={{ width: 14, height: 14, accentColor: '#7c3aed', cursor: 'pointer' }} />
            {o}
          </label>
        ))}
      </div>
    </F>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function StudentForm() {
  const { companyId, leadId, token, isIntake } = parseRoute();

  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [studentName,    setStudentName]    = useState('');
  const [companyName,    setCompanyName]    = useState('CAE');
  const [companyLogo,    setCompanyLogo]    = useState('');
  const [declaration,    setDeclaration]    = useState('');
  const [step,           setStep]           = useState(0);
  const [submitting,     setSubmitting]     = useState(false);
  const [submitted,      setSubmitted]      = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [prevFilled,     setPrevFilled]     = useState(null);
  const photoRef = useRef(null);

  const [form, setForm] = useState({
    // Personal
    firstName: '', lastName: '', dob: '', gender: '', nationality: '', passport: '',
    email: '', phone: '', address: '',
    // Parents
    fatherName: '', fatherPhone: '', fatherEmail: '', fatherOccupation: '',
    motherName: '', motherPhone: '', motherEmail: '', motherOccupation: '',
    // Academic history (table rows)
    eduHistory: BLANK_EDU_HISTORY,
    subjectsEnrolled: '',
    // Test scores
    ieltsScore: '', ieltsTestDate: '', ieltsLogin: '',
    toeflScore: '', toeflTestDate: '', toeflLogin: '',
    greScore: '', gmatScore: '',
    satAttempt1Verbal: '', satAttempt1Math: '', satAttempt1Total: '',
    satAttempt2Verbal: '', satAttempt2Math: '', satAttempt2Total: '',
    collegeBoardLogin: '',
    actScore: '',
    apTests: [{ subject: '', score: '', dateTaken: '' }],
    // Educational objective
    degreeTypes: [],
    intendedMajor1: '', intendedMajor2: '',
    semesterPreference: '', yearPreference: '',
    preferredLocations: '',
    budget: '', financialSupport: '',
    // Application accounts
    appEmail: '', appPassword: '',
    commonAppLogin: '', ucAppLogin: '',
    // Media
    testimonialText: '', testimonialVideoUrl: '',
  });
  const [photoUrl, setPhotoUrl] = useState('');

  const sv = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!companyId || !token || (!isIntake && !leadId)) {
      setError('Invalid form link. Please check the URL and try again.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/student-form/${companyId}/${leadId}/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load form');
        setStudentName(data.name || 'Student');
        if (data.companyName)    setCompanyName(data.companyName);
        if (data.companyLogoUrl) setCompanyLogo(data.companyLogoUrl);
        if (data.declarationText) setDeclaration(data.declarationText);
        if (data.profile) {
          setForm(f => ({
            ...f,
            ...data.profile,
            eduHistory: (data.profile.eduHistory?.length ? data.profile.eduHistory : BLANK_EDU_HISTORY),
            apTests:    (data.profile.apTests?.length    ? data.profile.apTests    : [{ subject: '', score: '', dateTaken: '' }]),
            degreeTypes: data.profile.degreeTypes || [],
          }));
          setPhotoUrl(data.profile.photoUrl || '');
        }
        if (data.filledAt) setPrevFilled(new Date(data.filledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch(`${BACKEND_URL}/api/student-form/${companyId}/${leadId}/${token}/photo`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Upload failed');
      setPhotoUrl(data.photoUrl);
    } catch (err) {
      alert('Photo upload failed: ' + err.message);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (isIntake && !form.firstName.trim()) {
      alert('Please enter your first name before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/student-form/${companyId}/${leadId}/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, photoUrl }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      alert('Submission failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const updEduRow = (i, updated) => {
    const rows = [...form.eduHistory];
    rows[i] = updated;
    sv('eduHistory', rows);
  };

  const updApRow = (i, updated) => {
    const rows = [...form.apTests];
    rows[i] = updated;
    sv('apTests', rows);
  };

  const addApRow = () => sv('apTests', [...form.apTests, { subject: '', score: '', dateTaken: '' }]);
  const removeApRow = (i) => sv('apTests', form.apTests.filter((_, idx) => idx !== i));

  // ── Loading / error / success ──────────────────────────────────────────────
  if (loading) return (
    <div style={centreScreen}>
      <div style={spinner} />
      <div style={{ fontSize: 14, color: '#64748b', marginTop: 16 }}>Loading your form…</div>
    </div>
  );

  if (error) return (
    <div style={centreScreen}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Link Error</div>
      <div style={{ fontSize: 14, color: '#64748b', maxWidth: 340, textAlign: 'center', lineHeight: 1.7 }}>{error}</div>
    </div>
  );

  if (submitted) return (
    <div style={centreScreen}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
        {isIntake ? `Welcome, ${form.firstName || 'Student'}!` : `Thank you, ${studentName.split(' ')[0]}!`}
      </div>
      <div style={{ fontSize: 14, color: '#64748b', maxWidth: 380, textAlign: 'center', lineHeight: 1.8 }}>
        {isIntake
          ? 'Your profile has been registered successfully. Your counselor will be in touch shortly to begin your university application journey.'
          : 'Your information has been submitted successfully. Your counselor will review it and follow up shortly.'}
      </div>
      <div style={{ marginTop: 24, padding: '14px 24px', borderRadius: 14, background: 'linear-gradient(135deg,rgba(124,58,237,0.08),rgba(59,130,246,0.08))', border: '1px solid #7c3aed30', fontSize: 13, color: '#7c3aed', fontWeight: 600 }}>
        ✅ Profile submitted to {companyName} counseling portal
      </div>
      <style>{`@keyframes bounceIn{0%{transform:scale(0.5);opacity:0}70%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#f8fafc 0%,#ede9fe 60%,#dbeafe 100%)', fontFamily: "'Inter',-apple-system,sans-serif" }}>
      <style>{`*{box-sizing:border-box}@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}.sf-card{animation:fadeUp 0.3s ease}@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#7c3aed 0%,#3b82f6 100%)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
          {companyLogo
            ? <img src={companyLogo} alt="logo" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#fff', padding: 3 }} />
            : <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#fff' }}>{companyName[0]}</div>
          }
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{companyName} — {isIntake ? 'Student Registration' : 'Student Profile'}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>University Admissions Counseling</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
          {isIntake ? '📋 New Student Registration' : `Hi, ${studentName.split(' ')[0]} 👋`}
        </div>
      </div>

      {/* Step bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 16px', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 0, minWidth: 'fit-content', maxWidth: 740, margin: '0 auto' }}>
          {STEPS.map((s, i) => (
            <button key={s.id} onClick={() => i <= step && setStep(i)}
              style={{ flex: 1, padding: '12px 6px', border: 'none', background: 'none', cursor: i <= step ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, borderBottom: `3px solid ${i === step ? '#7c3aed' : i < step ? '#10b981' : 'transparent'}`, transition: 'border-color 0.2s' }}>
              <span style={{ fontSize: 16 }}>{i < step ? '✅' : s.icon}</span>
              <span style={{ fontSize: 9, fontWeight: i === step ? 700 : 500, color: i === step ? '#7c3aed' : i < step ? '#10b981' : '#94a3b8', whiteSpace: 'nowrap' }}>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 16px 80px' }}>
        {prevFilled && step === 0 && (
          <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#059669', fontWeight: 600 }}>
            ✅ You previously submitted this form on {prevFilled}. You can update any field below.
          </div>
        )}

        <div className="sf-card" key={step}>

          {/* ── STEP 1: Personal ────────────────────────────────────────────── */}
          {step === 0 && (
            <Card title="Personal Details" subtitle="Please fill in your personal information accurately">
              <Grid>
                <F label="First Name" required><Inp value={form.firstName} onChange={v => sv('firstName', v)} placeholder="e.g. Rahul" /></F>
                <F label="Last Name" required><Inp value={form.lastName} onChange={v => sv('lastName', v)} placeholder="e.g. Sharma" /></F>
              </Grid>
              <Grid>
                <F label="Date of Birth"><Inp type="date" value={form.dob} onChange={v => sv('dob', v)} /></F>
                <F label="Gender"><Sel value={form.gender} onChange={v => sv('gender', v)} options={['Male','Female','Non-binary','Prefer not to say']} placeholder="Select gender" /></F>
              </Grid>
              <Grid>
                <F label="Nationality"><Inp value={form.nationality} onChange={v => sv('nationality', v)} placeholder="e.g. Indian" /></F>
                <F label="Passport Number"><Inp value={form.passport} onChange={v => sv('passport', v)} placeholder="e.g. A1234567" /></F>
              </Grid>
              <Grid>
                <F label="Email Address" required={isIntake}>
                  <Inp type="email" value={form.email} onChange={v => sv('email', v)} placeholder="student@email.com" />
                </F>
                <F label="Phone / Mobile">
                  <Inp value={form.phone} onChange={v => sv('phone', v)} placeholder="+91 98765 43210" />
                </F>
              </Grid>
              <F label="Full Residential Address">
                <textarea value={form.address || ''} onChange={e => sv('address', e.target.value)}
                  placeholder="Door No., Street, City, State, PIN Code"
                  style={{ ...inputCss, minHeight: 72, resize: 'vertical' }}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </F>
            </Card>
          )}

          {/* ── STEP 2: Parents ─────────────────────────────────────────────── */}
          {step === 1 && (
            <Card title="Parent / Guardian Details" subtitle="Help us stay in touch with your parents or guardians">
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Father / Guardian 1</div>
              <Grid>
                <F label="Name"><Inp value={form.fatherName} onChange={v => sv('fatherName', v)} placeholder="Father's full name" /></F>
                <F label="Phone"><Inp value={form.fatherPhone} onChange={v => sv('fatherPhone', v)} placeholder="+91 98765 43210" /></F>
              </Grid>
              <Grid>
                <F label="Email"><Inp type="email" value={form.fatherEmail} onChange={v => sv('fatherEmail', v)} placeholder="father@email.com" /></F>
                <F label="Occupation"><Inp value={form.fatherOccupation} onChange={v => sv('fatherOccupation', v)} placeholder="e.g. Business" /></F>
              </Grid>
              <Divider label="Mother / Guardian 2" />
              <Grid>
                <F label="Name"><Inp value={form.motherName} onChange={v => sv('motherName', v)} placeholder="Mother's full name" /></F>
                <F label="Phone"><Inp value={form.motherPhone} onChange={v => sv('motherPhone', v)} placeholder="+91 98765 43210" /></F>
              </Grid>
              <Grid>
                <F label="Email"><Inp type="email" value={form.motherEmail} onChange={v => sv('motherEmail', v)} placeholder="mother@email.com" /></F>
                <F label="Occupation"><Inp value={form.motherOccupation} onChange={v => sv('motherOccupation', v)} placeholder="e.g. Teacher" /></F>
              </Grid>
            </Card>
          )}

          {/* ── STEP 3: Academic History ─────────────────────────────────────── */}
          {step === 2 && (
            <Card title="Educational History" subtitle="Fill in your academic background level by level">
              <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {['Level', 'School / Institution Name', 'Location', 'From (mm/yy)', 'To (mm/yy)', 'Curriculum', '% / GPA'].map(h => (
                        <th key={h} style={{ padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.eduHistory.map((row, i) => (
                      <EduRow key={row.level} row={row} onChange={updated => updEduRow(i, updated)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <F label="Subjects currently enrolled in">
                <textarea value={form.subjectsEnrolled || ''} onChange={e => sv('subjectsEnrolled', e.target.value)}
                  placeholder="e.g. Physics, Chemistry, Maths, Computer Science, English"
                  style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </F>
              <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1d4ed8' }}>
                💡 If results are still awaited, enter expected percentage and note "expected" in the % field.
              </div>
            </Card>
          )}

          {/* ── STEP 4: Test Scores ──────────────────────────────────────────── */}
          {step === 3 && (
            <Card title="Standardised Test Scores" subtitle="Enter official scores — leave blank if not yet taken">

              <Divider label="TOEFL / IELTS" />
              <Grid>
                <F label="IELTS Score"><Inp value={form.ieltsScore} onChange={v => sv('ieltsScore', v)} placeholder="e.g. 7.5 / 9.0" /></F>
                <F label="IELTS Test Date"><Inp type="date" value={form.ieltsTestDate} onChange={v => sv('ieltsTestDate', v)} /></F>
              </Grid>
              <F label="IELTS Login Credentials">
                <Inp value={form.ieltsLogin} onChange={v => sv('ieltsLogin', v)} placeholder="Username / Password (optional)" />
              </F>
              <Grid>
                <F label="TOEFL iBT Score"><Inp value={form.toeflScore} onChange={v => sv('toeflScore', v)} placeholder="e.g. 105 / 120" /></F>
                <F label="TOEFL Test Date"><Inp type="date" value={form.toeflTestDate} onChange={v => sv('toeflTestDate', v)} /></F>
              </Grid>
              <F label="TOEFL Login Credentials">
                <Inp value={form.toeflLogin} onChange={v => sv('toeflLogin', v)} placeholder="Username / Password (optional)" />
              </F>

              <Divider label="SAT" />
              <div style={{ background: '#fafafa', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Attempt 1</div>
                <Grid cols={3}>
                  <F label="Verbal"><Inp value={form.satAttempt1Verbal} onChange={v => sv('satAttempt1Verbal', v)} placeholder="e.g. 720" /></F>
                  <F label="Math"><Inp value={form.satAttempt1Math} onChange={v => sv('satAttempt1Math', v)} placeholder="e.g. 740" /></F>
                  <F label="Total"><Inp value={form.satAttempt1Total} onChange={v => sv('satAttempt1Total', v)} placeholder="e.g. 1460" /></F>
                </Grid>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', margin: '10px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Attempt 2</div>
                <Grid cols={3}>
                  <F label="Verbal"><Inp value={form.satAttempt2Verbal} onChange={v => sv('satAttempt2Verbal', v)} placeholder="e.g. 750" /></F>
                  <F label="Math"><Inp value={form.satAttempt2Math} onChange={v => sv('satAttempt2Math', v)} placeholder="e.g. 760" /></F>
                  <F label="Total"><Inp value={form.satAttempt2Total} onChange={v => sv('satAttempt2Total', v)} placeholder="e.g. 1510" /></F>
                </Grid>
              </div>
              <F label="Collegeboard Login">
                <Inp value={form.collegeBoardLogin} onChange={v => sv('collegeBoardLogin', v)} placeholder="Email / Password" />
              </F>

              <Divider label="GRE / GMAT / ACT" />
              <Grid cols={3}>
                <F label="GRE Total"><Inp value={form.greScore} onChange={v => sv('greScore', v)} placeholder="e.g. 325 / 340" /></F>
                <F label="GMAT Total"><Inp value={form.gmatScore} onChange={v => sv('gmatScore', v)} placeholder="e.g. 700 / 800" /></F>
                <F label="ACT Composite"><Inp value={form.actScore} onChange={v => sv('actScore', v)} placeholder="e.g. 32 / 36" /></F>
              </Grid>

              <Divider label="AP Exams" />
              <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {['Subject', 'Score (1-5)', 'Date Taken'].map(h => (
                        <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.apTests.map((row, i) => (
                      <APRow key={i} row={row} onChange={updated => updApRow(i, updated)}
                        onRemove={() => removeApRow(i)} showRemove={form.apTests.length > 1} />
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={addApRow} style={{ fontSize: 12, color: '#7c3aed', background: 'rgba(124,58,237,0.07)', border: '1px dashed #7c3aed', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontWeight: 600 }}>
                + Add AP Subject
              </button>

              <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b45309', marginTop: 16 }}>
                Only enter scores from official test reports.
              </div>
            </Card>
          )}

          {/* STEP 5: Educational Objective */}
          {step === 4 && (
            <Card title="Educational Objective" subtitle="Tell us about your academic goals and preferences">
              <CheckGroup
                label="Course / Program Applying For"
                options={['BBA / BS', 'Transfer', 'MBA', 'MS', 'MA', 'PhD', 'Other']}
                selected={form.degreeTypes}
                onChange={v => sv('degreeTypes', v)}
              />
              <Grid>
                <F label="Intended Major — First Preference" required>
                  <Inp value={form.intendedMajor1} onChange={v => sv('intendedMajor1', v)} placeholder="e.g. Computer Science" />
                </F>
                <F label="Intended Major — Second Preference">
                  <Inp value={form.intendedMajor2} onChange={v => sv('intendedMajor2', v)} placeholder="e.g. Data Science" />
                </F>
              </Grid>
              <Grid>
                <F label="Semester You Wish to Begin">
                  <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                    {['Spring', 'Fall', 'Summer'].map(s => (
                      <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#334155' }}>
                        <input type="radio" name="semester" checked={form.semesterPreference === s} onChange={() => sv('semesterPreference', s)} style={{ accentColor: '#7c3aed' }} />
                        {s}
                      </label>
                    ))}
                  </div>
                </F>
                <F label="Year">
                  <Sel value={form.yearPreference} onChange={v => sv('yearPreference', v)}
                    options={['2025', '2026', '2027', '2028', '2029']} placeholder="Select year" />
                </F>
              </Grid>
              <F label="Countries, States or Cities Preferred (if any)">
                <Inp value={form.preferredLocations} onChange={v => sv('preferredLocations', v)} placeholder="e.g. USA (California, New York), Canada" />
              </F>
              <Grid>
                <F label="Budget (US$ per academic year)" required>
                  <Inp value={form.budget} onChange={v => sv('budget', v)} placeholder="e.g. 40000" />
                </F>
                <F label="Financial Support Will Be Provided By">
                  <Inp value={form.financialSupport} onChange={v => sv('financialSupport', v)} placeholder="e.g. Parents, Scholarship, Loan" />
                </F>
              </Grid>
              <Divider label="Application Accounts" />
              <Grid>
                <F label="Email Created for Applications">
                  <Inp type="email" value={form.appEmail} onChange={v => sv('appEmail', v)} placeholder="applications@gmail.com" />
                </F>
                <F label="Password">
                  <Inp value={form.appPassword} onChange={v => sv('appPassword', v)} placeholder="Keep this secure" />
                </F>
              </Grid>
              <Grid>
                <F label="Common App Login">
                  <Inp value={form.commonAppLogin} onChange={v => sv('commonAppLogin', v)} placeholder="Email / Username" />
                </F>
                <F label="UC Application Login">
                  <Inp value={form.ucAppLogin} onChange={v => sv('ucAppLogin', v)} placeholder="Email / Username" />
                </F>
              </Grid>
            </Card>
          )}

          {/* STEP 6: Photo & Story */}
          {step === 5 && (
            <Card title="Your Photo & Story" subtitle="Share your profile photo and experience">
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 10 }}>Passport Size Photograph</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                  <div onClick={() => !photoUploading && photoRef.current && photoRef.current.click()}
                    style={{ width: 90, height: 110, borderRadius: 10, border: '2px dashed ' + (photoUrl ? '#10b981' : '#cbd5e1'), background: photoUrl ? '#f0fdf4' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
                    {photoUrl
                      ? <img src={photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ textAlign: 'center', padding: 8 }}><div style={{ fontSize: 26 }}>📷</div><div style={{ fontSize: 9, color: '#94a3b8', marginTop: 3 }}>Click to upload</div></div>
                    }
                    {photoUploading && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={spinner} />
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                    <button onClick={() => !isIntake && !photoUploading && photoRef.current && photoRef.current.click()} disabled={isIntake || photoUploading}
                      style={{ padding: '9px 18px', borderRadius: 9, border: '1.5px solid #7c3aed', background: 'rgba(124,58,237,0.07)', color: '#7c3aed', fontWeight: 700, fontSize: 12, cursor: photoUploading ? 'wait' : 'pointer', display: 'block', marginBottom: 6 }}>
                      {isIntake ? 'Available after submission' : photoUploading ? 'Uploading...' : photoUrl ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    {photoUrl && <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Photo uploaded</div>}
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>JPEG/PNG · Max 10 MB · Passport size preferred</div>
                  </div>
                </div>
              </div>

              <F label="Your Experience (Optional)">
                <textarea value={form.testimonialText || ''} onChange={e => sv('testimonialText', e.target.value)}
                  placeholder="Share how our counseling helped you. What did you find most valuable?"
                  style={{ ...inputCss, minHeight: 100, resize: 'vertical', lineHeight: 1.6 }}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </F>
              <F label="Video Testimonial Link (Optional)">
                <Inp value={form.testimonialVideoUrl} onChange={v => sv('testimonialVideoUrl', v)} placeholder="https://youtube.com/watch?v=..." />
              </F>

              <div style={{ marginTop: 24, background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Declaration</div>
                <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.8, margin: 0 }}>
                  {declaration ||
                    'I hereby declare that the information provided above is true and accurate to the best of my knowledge. I understand that any false or misleading information may result in the rejection of my application or cancellation of any offer received. I consent to the use of this information for the purpose of university counseling and applications.'}
                </p>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, cursor: 'pointer' }}>
                  <input type="checkbox" style={{ marginTop: 2, accentColor: '#7c3aed', cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, color: '#334155', fontWeight: 600, lineHeight: 1.6 }}>
                    I have read and agree to the above declaration.
                  </span>
                </label>
              </div>
            </Card>
          )}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
          {step > 0
            ? <button onClick={() => setStep(s => s - 1)} style={{ padding: '11px 22px', borderRadius: 11, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Back</button>
            : <div />
          }
          {step < STEPS.length - 1
            ? <button onClick={() => setStep(s => s + 1)} style={{ padding: '11px 28px', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#3b82f6)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(124,58,237,0.3)' }}>Next</button>
            : <button onClick={handleSubmit} disabled={submitting} style={{ padding: '11px 32px', borderRadius: 11, border: 'none', background: submitting ? '#94a3b8' : 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', boxShadow: submitting ? 'none' : '0 4px 14px rgba(16,185,129,0.35)' }}>
                {submitting ? 'Submitting...' : 'Submit Profile'}
              </button>
          }
        </div>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#94a3b8' }}>Step {step + 1} of {STEPS.length}</div>
      </div>
    </div>
  );
}

const centreScreen = { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#f8fafc,#ede9fe)', fontFamily: "'Inter',sans-serif", padding: 24 };
const spinner = { width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(124,58,237,0.15)', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' };
